import { DocumentCitationStyleLabels, type DocumentConfig,DocumentLanguageLabels } from '@alpod-ai-writer/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Button, Label, Select, Textarea, TextInput } from 'flowbite-react';
import { AlertTriangleIcon, FileSpreadsheetIcon, LifeBuoyIcon } from 'lucide-react';
import { type FC,useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { useApi } from '../../../api/useApi.hook';
import { ErrorSwitch } from '../../../components/errorMessage/errorSwitch.component';
import { Loading } from '../../../components/loading/loading.component';
import { ConfirmationModal } from '../../../components/modals/confirmation/confirmationModal.component';
import { formatDate } from '../../../editor/utils/string.utils';
import { useModals } from '../../../hooks/useModals.hook';
import { useNavBlockerSetter } from '../../../hooks/useNavBlocker.hook';
import { sortedDocumentTypeKeys } from '../../../lib/template.utils';
import { ToastNotificationType } from '../../components/toastNotification/toastNotifications.context';
import { useToastNotifications } from '../../components/toastNotification/useToastNotifications.hook';
import { useSession } from '../../hooks/useSession.hook';
import { MissingTemplateModal } from '../../modals/feedback/missingTemplateModal.component';
import { RequestTemplateChangeModal } from '../../modals/feedback/requestTemplateChangeModal.component';
import { RouteIndex } from '../../routeIndex';

interface FormValues {
    documentName: string;
    templateId: string;
    documentType: string;
    goal: string;
}

export interface DocumentSettingsProps {
    documentId: string;
    className?: string;
    onDownloadJson?: () => void;
}

export const DocumentSettings: FC<DocumentSettingsProps> = ({ documentId, className, onDownloadJson }) => {
    const queryClient = useQueryClient();
    const api = useApi();
    const modals = useModals();
    const toasts = useToastNotifications();
    const navigate = useNavigate();
    const { identity } = useSession();

    const { data: document, isLoading, error } = useQuery({
        queryKey: ['document', documentId],
        queryFn: async () => {
            return api.v1.documents.getById(documentId);
        }
    });

    const { register, handleSubmit, formState: { errors, isDirty }, watch, reset, control } = useForm<FormValues>({
        defaultValues: {
            documentName: document?.documentName ?? '',
            templateId: document?.templateId ?? '',
            documentType: document?.documentType ?? '',
            goal: document?.goal ?? ''
        }
    });

    useNavBlockerSetter('documentSettings', isDirty);

    const templateId = watch('templateId');

    const templateList = useQuery({
        queryKey: [ 'documentTemplateList' ],
        queryFn: () => api.v1.templates.getAll()
    });

    const templateDetail = useQuery({
        queryKey: [ 'documentTemplate', templateId ],
        enabled: !!templateId,
        queryFn: () => api.v1.templates.getById(templateId)
    });

    const reload = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['document', documentId] });
    }, [ queryClient, documentId ]);

    const saveDocument = useMutation({
        mutationFn: (data: FormValues) => api.v1.documents.updateById(documentId, { ...data }),
        onSuccess: (data) => {
            reload();

            reset({
                documentName: data.documentName ?? '',
                templateId: data.templateId ?? '',
                documentType: data.documentType ?? '',
                goal: data.goal ?? ''
            });

            toasts.showToast({
                type: ToastNotificationType.SUCCESS,
                message: 'Nastavení dokumentu bylo úspěšně uloženo.'
            });
        }
    });

    const deleteDocument = useMutation({
        mutationFn: () => api.v1.documents.deleteById(documentId),
        onSuccess: () => {
            toasts.showToast({
                type: ToastNotificationType.SUCCESS,
                message: 'Dokument byl úspěšně smazán.'
            });
        },
        onError: (err) => {
            console.error('Failed to delete document', err);

            toasts.showToast({
                type: ToastNotificationType.ERROR,
                message: 'Při mazání dokumentu došlo k chybě. Zkuste to prosím za chvíli znovu.'
            });
        }
    });

    const openInvoice = useMutation({
        mutationFn: () => api.v1.documents.getInvoiceUrl(documentId),
        onSuccess: (data) => {
            window.open(data.url, '_blank');
        },
        onError: (err) => {
            console.error('Failed to get document invoice URL', err);

            toasts.showToast({
                type: ToastNotificationType.ERROR,
                message: 'Nepodařilo se načíst fakturu. Kontaktujte prosím podporu.'
            });
        }
    });

    const handleDeleteClick = useCallback(() => {
        modals.showModal('delete-document-confirmation', ConfirmationModal, {
            message: 'Opravdu chcete smazat tento dokument? Tuto akci nelze vzít zpět.',
            isDanger: true,
            confirmLabel: 'Nenávratně smazat',
            cancelLabel: 'Zrušit akci',
            onConfirm: async () => {
                deleteDocument.mutate();
                queryClient.invalidateQueries({ queryKey: ['my-documents'] });

                navigate(RouteIndex.documentList);
                return true;
            }
        });
    }, [ deleteDocument, modals ]);

    const handleOpenInvoice = useCallback(() => {
        openInvoice.mutate();
    }, [ openInvoice ]);

    const onSubmit = async (data: FormValues) => {
        saveDocument.mutate(data);
    };

    const handleReset = useCallback(() => {
        reset({
            documentName: document?.documentName ?? '',
            templateId: document?.templateId ?? '',
            documentType: document?.documentType ?? '',
            goal: document?.goal ?? ''
        });
    }, [ reset, document ]);

    const handleMissingTemplate = useCallback(() => {
        modals.showModal('missing-template', MissingTemplateModal, {
            locationInApp: 'Nový dokument'
        });
    }, []);

    const handleRequestTemplateChange = useCallback(() => {
        modals.showModal('request-template-change', RequestTemplateChangeModal, {
            documentId
        });
    }, []);

    const documentType = watch('documentType');
    const templateConfig = templateDetail.data?.config as DocumentConfig|undefined;
    const templateTypeConfig = templateConfig?.types?.[documentType];

    const readOnly = !document?.canEdit;
    const isAdmin = identity?.systemPrivileges['Documents'] === 'Manage';
    const isOwnerOrAdmin = (
        document?.ownerIdentityId === identity?.identityId || isAdmin
    );

    const canChangeTemplate = document?.paidForAt === null || isAdmin;

    return (
        <>
            {!document && isLoading && (
                <div className='flex flex-row items-center justify-center min-h-screen'>
                    <Loading label='Načítání dat...' />
                </div>
            )}

            <ErrorSwitch
                error={error}
                default={{ message: 'Při načítání dat došlo k chybě. Zkuste to prosím za chvíli znovu.', showDetails: true }}
                options={[]}
                retry={reload}
            />

            <form onSubmit={handleSubmit(onSubmit)} className={clsx(
                'flex flex-col gap-6',
                className
            )}>
                <div>
                    <Label htmlFor="documentName" className="block mb-1">Název dokumentu</Label>
                    <TextInput
                        id="documentName"
                        type="text"
                        sizing='lg'
                        placeholder="Bakalářská práce na téma..."
                        {...register('documentName', { required: 'Název je povinný.' })}
                        color={errors.documentName ? 'failure' : 'default'}
                        readOnly={readOnly}
                        helperText={(
                            <>
                                {errors.documentName && (
                                    <span className='block'>
                                        {errors.documentName.message}
                                    </span>
                                )}
                            </>
                        )}
                    />
                </div>

                <div>
                    <Label htmlFor="templateId" className="block mb-1">Vyberte šablonu podle Vaší školy a fakulty</Label>
                    <Controller
                        name="templateId"
                        control={control}
                        rules={{ required: 'Šablona je povinná.' }}
                        render={({ field }) => (
                            <Select
                                id="templateId"
                                {...field}
                                color={errors.templateId ? 'failure' : 'default'}
                                disabled={readOnly || !canChangeTemplate}
                                helperText={(
                                    <>
                                        {errors.templateId && (
                                            <span className='block'>
                                                {errors.templateId.message}
                                            </span>
                                        )}
                                    </>
                                )}
                            >
                                <option value='' disabled>Vyberte šablonu</option>
                                {templateList.data?.items.map((template) => (
                                    <option key={template.templateId} value={template.templateId}>{template.label}</option>    
                                ))}
                            </Select>
                        )}
                    />

                    {templateDetail.data && !templateDetail.data.docxPath && (
                        <p className='flex flex-row item-center gap-2 text-red-600 font-semibold'>
                            <AlertTriangleIcon size={20} />
                            <span>Pozor: Tato šablona nemá funkční konfiguraci.</span>
                        </p>
                    )}
                </div>

                <div>
                    <Label htmlFor="documentType" className="block mb-1">Typ dokumentu</Label>
                    <Controller
                        name="documentType"
                        control={control}
                        rules={{ required: 'Typ dokumentu je povinný.' }}
                        render={({ field }) => (
                            <Select
                                id="documentType"
                                {...field}
                                color={errors.documentType ? 'failure' : 'default'}
                                disabled={!templateConfig || readOnly || !canChangeTemplate}
                                helperText={(
                                    <>
                                        {errors.documentType && (
                                            <span className='block'>
                                                {errors.documentType.message}
                                            </span>
                                        )}
                                        <span className='block font-semibold'>Pozor: po zaplacení dokumentu již nelze šablonu ani typ změnit.</span>
                                    </>
                                )}
                            >
                                <option value='' disabled>Vyberte typ dokumentu</option>
                                {templateConfig?.types && sortedDocumentTypeKeys(templateConfig.types).map((type) => (
                                    <option key={type} value={type}>{templateConfig.types[type].label}</option>
                                ))}
                            </Select>
                        )}
                    />
                </div>

                {canChangeTemplate && (
                    <>
                        <div className='flex flex-col md:flex-row items-center gap-4'>
                            <span className='text-sm font-medium text-orange-600 text-center md:text-left'>Nenašli jste Vaši školu nebo fakultu? Napište nám a my ji doplníme.</span>
                            <Button
                                color='help'
                                className='flex-shrink-0'
                                onClick={handleMissingTemplate}
                            ><LifeBuoyIcon size={18} /> <span className='ml-2'>Chybí mi šablona</span></Button>
                        </div>
                    </>
                )}

                {!canChangeTemplate && (
                    <div className='flex flex-col md:flex-row items-center gap-4'>
                        <span className='text-sm font-medium text-orange-600 text-center md:text-left'>Šablonu po úhradě dokumentu již nelze změnit. Pokud šablonu potřebujete přesto změnit, kontaktujte nás prosím.</span>
                        <Button
                            color='help'
                            className='flex-shrink-0'
                            onClick={handleRequestTemplateChange}
                        ><LifeBuoyIcon size={18} /> <span className='ml-2'>Chci změnit šablonu</span></Button>
                    </div>
                )}

                {templateConfig && templateTypeConfig && (
                    <div className='flex flex-col md:flex-row md:items-center justify-between gap-2 gap-y-4 text-sm p-4 border border-gray-300 rounded-md'>
                        <div className='flex flex-col gap-1'>
                            <span className='font-semibold'>Jazyk dokumentu</span>
                            <span className='font-medium'>{DocumentLanguageLabels[templateConfig.language] ?? templateConfig.language}</span>
                        </div>
                        <div className='flex flex-col gap-1'>
                            <span className='font-semibold'>Rozsah</span>
                            <span className='font-medium'>
                                {templateTypeConfig.minPages}
                                {templateTypeConfig.maxPages ? ` - ${templateTypeConfig.maxPages}` : ''} normostran
                            </span>
                        </div>
                        <div className='flex flex-col gap-1'>
                            <span className='font-semibold'>Citační norma</span>
                            <span className='font-medium'>{DocumentCitationStyleLabels[templateConfig.citationStyle] ?? templateConfig.citationStyle}</span>
                        </div>
                    </div>
                )}

                <div>
                    <Label htmlFor="goal" className="block mb-1">Cíl dokumentu</Label>
                    <Textarea
                        id="goal"
                        {...register('goal', {
                            maxLength: { value: 10000, message: 'Maximální délka je 10000 znaků' }
                        })}
                        placeholder='Cílem práce je popsat a analyzovat dopad X na Y...'
                        rows={6}
                        color={errors.goal ? 'failure' : 'default'}
                        readOnly={readOnly}
                        helperText={(
                            <>
                                <span className='block text-sm'>Popište v několika větách, o čem je Vaše práce. Zadaný popis pomáhá naší AI lépe pochopit cíl Vaší práce.</span>
                                {errors.goal && (
                                    <span className='block'>
                                        {errors.goal.message}
                                    </span>
                                )}
                            </>
                        )}
                    />
                </div>

                {isOwnerOrAdmin && document?.paidForAt && (
                    <div className='flex flex-col md:flex-row items-center gap-4 text-sm px-5 py-4 rounded-md border border-gray-300'>
                        <span className='text-lime-700 font-medium grow'>Za dokument jsme přijali platbu {formatDate(document.paidForAt)}.</span>
                        {document.hasInvoice && (
                            <button
                                type='button'
                                className='flex flex-row items-center gap-1 hyperlink'
                                onClick={handleOpenInvoice}
                            ><FileSpreadsheetIcon size={20} /> Zobrazit fakturu</button>
                        )}
                    </div>
                )}


                {!readOnly && (
                    <div className='flex flex-row items-center justify-center gap-4 lg:gap-6'>
                        {isOwnerOrAdmin && (
                            <button
                                type='button'
                                className='text-red-700 underline hover:no-underline cursor-pointer'
                                onClick={handleDeleteClick}
                            >Smazat dokument</button>
                        )}
                        <div className='grow' />
                        {isDirty && (
                            <button
                                type='button'
                                className='text-gray-700 underline hover:no-underline cursor-pointer'
                                onClick={handleReset}
                            >Zahodit změny</button>
                        )}
                        <Button
                            type='submit'
                            size='lg'
                            color='primary'
                            disabled={saveDocument.isPending}
                        >Uložit nastavení</Button>
                    </div>
                )}

                <ErrorSwitch
                    error={saveDocument.error}
                    default={{ message: 'Při ukládání dat došlo k chybě. Zkuste to prosím za chvíli znovu.', showDetails: true }}
                    options={[]}
                />
            </form>
        </>
    );
};
